import { browser } from "$app/environment"
import { isDevelopment, isTesting } from "$lib/environment"
import session from "$lib/tastyworks-rest/session"
import type { UtmParamKey } from "$lib/utm/utm-capture"
import type {
  BuyingPowerEventName,
  BuyingPowerEventPayload,
} from "@tastyworks/shared-user-analytics/buying-power"
import {
  bufferedGtag,
  flushGtmBuffer,
} from "@tastyworks/shared-user-analytics/gtm"
import type { AccountOpeningApplication } from "@tastyworks/tastyworks-api"
import type { EmptyObject } from "type-fest"

export {
  bufferedGtag,
  flushGtmBuffer,
  gtag,
  loadGoogleTagManager,
} from "@tastyworks/shared-user-analytics/gtm"

export function trackMoneyMovement(
  success: boolean,
  step: string,
  data: {
    external_account_id: string
    transaction_amount: number
    transaction_institution: string
    transaction_id?: string
    failure_reason?: string
  }
) {
  const outcome = success ? "completed" : "failed"

  const trackingData = {
    ...data,
    // FormCurrencyInput's IMask wires unmaskedValue (a string) back into the
    // form state, so by the time we get here transaction_amount is the
    // declared `number` per TS but a string at runtime. Coerce so snowplow
    // sees a number, not "21000".
    transaction_amount: Number(data.transaction_amount),
    is_webview_event: false,
    transaction_direction: "INCOMING",
    transaction_type: "ACH",
  }

  trackEvent(`money_movement_${outcome}`, step, trackingData)
}

export function trackApplicationCompletion(data: {
  is_auto_approved: boolean
  has_timed_out: boolean
  prior_applications: AccountOpeningApplication[]
  application_id?: string
  age: number
  netWorth: number
}) {
  trackEvent("application_completed", "done", data)
}

export function trackStepCompletion(step: string, eventParams?: object) {
  trackEvent("step_completed", step, { eventParams })
}

export function trackNavigation(step: string, title: string, path: string) {
  trackEvent("navigation", step, { title, path })
}

export type SupportType = "help center" | "call support"
export type SupportTopic = "general" | "account type"

export function trackSupportLinkClick(
  step: string,
  support_type: SupportType,
  support_topic: SupportTopic = "general"
) {
  trackEvent("application_support", step, {
    support_type,
    support_topic: support_type === "help center" ? support_topic : null,
  })
}

export function trackSkipStep(step: string, eventParams?: object) {
  trackEvent("skip_step", step, eventParams)
}

export function trackLaunchDarklyFlags(flags: object) {
  trackEvent("launch_darkly_flags", "", { flags })
}

export function trackCTAClick(step: string, cta: string) {
  trackEvent("call_to_action_clicked", step, { cta })
}

export function trackSignupCompletion(
  email: string,
  utmParams: Partial<Record<UtmParamKey, string>>,
  promoCode?: string,
  externalId?: string
) {
  if (!hasConsent(OneTrustCategory.targeting)) {
    return
  }

  trackEvent("prospect_created", "signup", {
    email,
    ...utmParams,
    promo_code: promoCode,
    tasty_user_id: externalId,
  })
}

export function trackBuyingPower(
  eventName: BuyingPowerEventName,
  step: string,
  payload: BuyingPowerEventPayload
) {
  trackEvent(eventName, step, payload)
}

export function trackEvent(
  event: GTMEventType,
  step: string,
  eventParams?: object
) {
  bufferedGtag(new GTMEvent(event, step, eventParams))
}

type GTMEventType =
  | "application_completed"
  | "application_submitted"
  | "step_completed"
  | "navigation"
  | "application_support"
  | "skip_step"
  | "launch_darkly_flags"
  | "call_to_action_clicked"
  | "money_movement_completed"
  | "money_movement_failed"
  | "prospect_created"
  | "application_started"
  | BuyingPowerEventName

// do NOT change names for this constructor, GTM requires specific naming conventions data team
class GTMEvent<T extends object = EmptyObject> {
  public readonly application_name = "AO2"
  public readonly application_version = __APP_VERSION__
  public readonly tasty_user_id = session.user.externalId

  constructor(
    public event: GTMEventType,
    public readonly application_step: string,
    payload?: T
  ) {
    if (payload) {
      Object.assign(this, payload)
    }
  }
}

/**
 * C0001 - Strictly Necessary
 * C0002 - Performance Cookies
 * C0003 - Functional Cookies
 * C0004 - Targeting Cookies
 * C0005 - Social Media Cookies
 */
export enum OneTrustCategory {
  strictlyNecessary = "C0001",
  performance = "C0002",
  functional = "C0003",
  targeting = "C0004",
  socialMedia = "C0005",
}

// Populated by Analytics.svelte's OneTrustGroupsUpdated handler. undefined
// until the user's first consent decision (or the webview's synthetic one)
// arrives.
let grantedCategories: OneTrustCategory[] | undefined

export function setGrantedCategories(
  categories: OneTrustCategory[] | undefined
) {
  grantedCategories = categories
}

export function hasConsent(category: OneTrustCategory) {
  return (grantedCategories ?? []).includes(category)
}

// flush the buffer for debugging and testing
if (
  browser &&
  window.gtmBuffer &&
  window.dataLayer &&
  (isDevelopment() || isTesting())
) {
  flushGtmBuffer()
}

/* v8 ignore start -- @preserve */
if (import.meta.vitest) {
  const { test, expect, beforeAll, beforeEach, describe, afterAll } =
    import.meta.vitest

  describe("events", () => {
    beforeAll(() => {
      window.gtmLoaded = true
      window.dataLayer = []
    })

    afterAll(() => {
      window.gtmLoaded = false
    })

    test("trackApplicationCompletion", () => {
      trackApplicationCompletion({
        is_auto_approved: true,
        has_timed_out: false,
        prior_applications: [],
        age: 30,
        netWorth: 50001,
      })
      expect(window.dataLayer?.at(-1)).toEqual(
        expect.objectContaining({
          event: "application_completed",
          application_step: "done",
          is_auto_approved: true,
          has_timed_out: false,
          prior_applications: [],
          age: 30,
          netWorth: 50001,
        })
      )
    })

    test("trackStepCompletion", () => {
      trackStepCompletion("personalInfo.contactInformation", { country: "USA" })
      expect(window.dataLayer?.at(-1)).toEqual(
        expect.objectContaining({
          event: "step_completed",
          application_step: "personalInfo.contactInformation",
          eventParams: { country: "USA" },
        })
      )
    })

    test("trackNavigation", () => {
      trackNavigation(
        "personalInfo.contactInformation",
        "Personal Info",
        "/personal-info"
      )
      expect(window.dataLayer?.at(-1)).toEqual(
        expect.objectContaining({
          event: "navigation",
          application_step: "personalInfo.contactInformation",
          title: "Personal Info",
        })
      )
    })

    test("trackSupportLinkClick", () => {
      trackSupportLinkClick("personalInfo.contactInformation", "help center")
      expect(window.dataLayer?.at(-1)).toEqual(
        expect.objectContaining({
          event: "application_support",
          application_step: "personalInfo.contactInformation",
          support_type: "help center",
          support_topic: "general",
        })
      )
    })

    test("trackSkipStep", () => {
      trackSkipStep("personalInfo.contactInformation")
      expect(window.dataLayer?.at(-1)).toEqual(
        expect.objectContaining({
          event: "skip_step",
          application_step: "personalInfo.contactInformation",
        })
      )
    })

    test("trackLaunchDarklyFlags", () => {
      trackLaunchDarklyFlags({ "feature-flag": true })
      expect(window.dataLayer?.at(-1)).toEqual(
        expect.objectContaining({
          event: "launch_darkly_flags",
          application_step: "",
          flags: { "feature-flag": true },
        })
      )
    })

    test("trackCTAClick", () => {
      const ctaButton = "ig_uk_app_form"
      const stepName = "signup"

      trackCTAClick(stepName, ctaButton)
      expect(window.dataLayer?.at(-1)).toEqual(
        expect.objectContaining({
          event: "call_to_action_clicked",
          application_step: stepName,
          cta: ctaButton,
        })
      )
    })

    test("trackMoneyMovement emits money_movement_completed with ACH/INCOMING context on success", () => {
      trackMoneyMovement(true, "setup.funding.confirmDeposit", {
        external_account_id: "ext-001",
        transaction_amount: 150,
        transaction_institution: "Chase",
      })

      expect(window.dataLayer?.at(-1)).toEqual(
        expect.objectContaining({
          event: "money_movement_completed",
          application_step: "setup.funding.confirmDeposit",
          external_account_id: "ext-001",
          transaction_amount: 150,
          transaction_institution: "Chase",
          is_webview_event: false,
          transaction_direction: "INCOMING",
          transaction_type: "ACH",
        })
      )
    })

    test("trackMoneyMovement coerces string transaction_amount to a number", () => {
      trackMoneyMovement(true, "setup.funding.confirmDeposit", {
        external_account_id: "ext-001",
        // simulates the IMask unmaskedValue path that leaks a string through
        // the form state despite the yup number() schema.
        transaction_amount: "21000" as unknown as number,
        transaction_institution: "Chase",
      })

      expect(window.dataLayer?.at(-1)).toEqual(
        expect.objectContaining({
          transaction_amount: 21000,
        })
      )
    })

    test("trackMoneyMovement emits money_movement_failed with the failure_reason on failure", () => {
      trackMoneyMovement(false, "setup.funding.confirmDeposit", {
        external_account_id: "ext-001",
        transaction_amount: 150,
        transaction_institution: "no_institution_available",
        failure_reason: "Insufficient funds",
      })

      expect(window.dataLayer?.at(-1)).toEqual(
        expect.objectContaining({
          event: "money_movement_failed",
          application_step: "setup.funding.confirmDeposit",
          external_account_id: "ext-001",
          transaction_amount: 150,
          transaction_institution: "no_institution_available",
          failure_reason: "Insufficient funds",
          is_webview_event: false,
          transaction_direction: "INCOMING",
          transaction_type: "ACH",
        })
      )
    })

    test("GTMEvent", () => {
      session.user.externalId = "12345"
      const event = new GTMEvent(
        "step_completed",
        "personalInfo.contactInformation",
        {
          country: "USA",
        }
      )
      expect(event).toEqual(
        expect.objectContaining({
          application_version: __APP_VERSION__,
          application_name: "AO2",
          tasty_user_id: "12345",
          event: "step_completed",
          application_step: "personalInfo.contactInformation",
          country: "USA",
        })
      )
    })

    test("trackBuyingPower emits a bp_* event wrapped in the AO2 envelope", () => {
      trackBuyingPower("bp_presented", "done.instantBuyingPower", {
        external_account_id: "ext-001",
        ach_relationship_id: 42,
        external_transaction_id: "deposit-1",
        occurred_at: 1_700_000_000_000,
        bp_amount: 500,
      })

      expect(window.dataLayer?.at(-1)).toEqual(
        expect.objectContaining({
          event: "bp_presented",
          application_name: "AO2",
          application_step: "done.instantBuyingPower",
          external_account_id: "ext-001",
          ach_relationship_id: 42,
          external_transaction_id: "deposit-1",
          bp_amount: 500,
        })
      )
    })

    test("trackBuyingPower forwards a bp_not_presented payload", () => {
      trackBuyingPower("bp_not_presented", "setup.funding.confirmDeposit", {
        external_account_id: "ext-001",
        occurred_at: 1_700_000_000_000,
        bp_amount: 0,
      })

      expect(window.dataLayer?.at(-1)).toEqual(
        expect.objectContaining({
          event: "bp_not_presented",
          application_step: "setup.funding.confirmDeposit",
          bp_amount: 0,
        })
      )
    })

    describe("trackSignupCompletion / consent gating", () => {
      beforeEach(() => {
        window.dataLayer = []
        setGrantedCategories(undefined)
      })

      test("fires prospect_created when targeting consent is granted", () => {
        setGrantedCategories([OneTrustCategory.targeting])

        trackSignupCompletion(
          "user@example.com",
          { utm_source: "google", utm_medium: "cpc" },
          "SAVE10",
          "ext-456"
        )

        expect(window.dataLayer?.at(-1)).toEqual(
          expect.objectContaining({
            event: "prospect_created",
            application_step: "signup",
            email: "user@example.com",
            utm_source: "google",
            utm_medium: "cpc",
            promo_code: "SAVE10",
            tasty_user_id: "ext-456",
          })
        )
      })

      test("does not fire prospect_created when targeting consent is not granted", () => {
        setGrantedCategories([OneTrustCategory.performance])

        trackSignupCompletion("user@example.com", {})

        expect(window.dataLayer).toHaveLength(0)
      })

      test("does not fire prospect_created when consent has not been decided yet", () => {
        trackSignupCompletion("user@example.com", {})

        expect(window.dataLayer).toHaveLength(0)
      })
    })
  })
}
